import { useJsApiLoader, Libraries } from '@react-google-maps/api'
import { useCallback } from 'react'

import { logger } from '@/utils/logger'

// Google Maps API libraries to load
const libraries: Libraries = ['places']

export interface AddressComponents {
  streetNumber?: string
  route?: string
  subpremise?: string
  locality?: string
  administrativeAreaLevel1?: string
  country?: string
  postalCode?: string
}

export interface ParsedAddress {
  streetAddress: string
  unit?: string
  city: string
  stateProvince: string
  country: string
  postalCode: string
}

export interface PlaceSuggestion {
  placeId: string
  description: string
  mainText: string
  secondaryText: string
}

/**
 * Custom hook for Google Places API integration using the new AutocompleteSuggestion API
 */
export const useGooglePlaces = () => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries,
  })

  /**
   * Gets autocomplete suggestions using Google Places API
   */
  const getPlaceSuggestions = useCallback(
    async (
      input: string,
      options?: { country?: string; types?: string[] }
    ): Promise<PlaceSuggestion[]> => {
      if (!isLoaded || !window.google || input.length < 3) {
        return []
      }

      return new Promise((resolve) => {
        try {
          const service = new google.maps.places.AutocompleteService()

          const request: google.maps.places.AutocompletionRequest = {
            input,
            types: options?.types || ['geocode'],
          }

          if (options?.country) {
            request.componentRestrictions = {
              country: options.country.toLowerCase(),
            }
          }

          service.getPlacePredictions(request, (predictions, status) => {
            if (
              status !== google.maps.places.PlacesServiceStatus.OK ||
              !predictions
            ) {
              logger.info('Places service returned non-OK status', { status })
              resolve([])
              return
            }

            const suggestions = predictions.map((prediction) => ({
              placeId: prediction.place_id,
              description: prediction.description,
              mainText: prediction.structured_formatting?.main_text || '',
              secondaryText:
                prediction.structured_formatting?.secondary_text || '',
            }))

            resolve(suggestions)
          })
        } catch (error) {
          logger.error('Error fetching place suggestions', error)
          resolve([])
        }
      })
    },
    [isLoaded]
  )

  /**
   * Gets place details and parses address components
   */
  const parseAddressComponents = useCallback(
    async (
      placeId: string,
      countryCode?: string
    ): Promise<ParsedAddress | null> => {
      if (!isLoaded || !window.google) {
        return null
      }

      try {
        const service = new google.maps.places.PlacesService(
          document.createElement('div')
        )

        return new Promise((resolve) => {
          service.getDetails(
            {
              placeId,
              fields: ['address_components', 'formatted_address'],
            },
            (place, status) => {
              if (
                status !== google.maps.places.PlacesServiceStatus.OK ||
                !place?.address_components
              ) {
                resolve(null)
                return
              }

              const componentMap: AddressComponents = {}

              // Map Google's component types to our structure
              for (const component of place.address_components) {
                const types = component.types

                if (types.includes('street_number')) {
                  componentMap.streetNumber = component.long_name
                } else if (types.includes('route')) {
                  componentMap.route = component.long_name
                } else if (types.includes('subpremise')) {
                  componentMap.subpremise = component.long_name
                } else if (types.includes('locality')) {
                  componentMap.locality = component.long_name
                } else if (
                  types.includes('sublocality') ||
                  types.includes('sublocality_level_1')
                ) {
                  // For places like Hong Kong, use sublocality as city if no locality is found
                  if (!componentMap.locality) {
                    componentMap.locality = component.long_name
                  }
                } else if (
                  types.includes('neighborhood') ||
                  types.includes('sublocality_level_2')
                ) {
                  // Use neighborhood or sublocality_level_2 as city if no locality is found
                  if (!componentMap.locality) {
                    componentMap.locality = component.long_name
                  }
                } else if (types.includes('administrative_area_level_1')) {
                  componentMap.administrativeAreaLevel1 = component.short_name
                } else if (types.includes('administrative_area_level_2')) {
                  // Some countries use admin_area_level_2 for state/province
                  if (!componentMap.administrativeAreaLevel1) {
                    componentMap.administrativeAreaLevel1 = component.short_name
                  }
                } else if (types.includes('country')) {
                  componentMap.country = component.short_name
                } else if (types.includes('postal_code')) {
                  componentMap.postalCode = component.long_name
                }
              }

              // Construct the parsed address
              const streetAddress = [
                componentMap.streetNumber,
                componentMap.route,
              ]
                .filter(Boolean)
                .join(' ')

              const result = {
                streetAddress: streetAddress || '',
                unit: componentMap.subpremise || '',
                city: componentMap.locality || '',
                stateProvince: componentMap.administrativeAreaLevel1 || '',
                country: componentMap.country || countryCode || '',
                postalCode: componentMap.postalCode || '',
              }

              resolve(result)
            }
          )
        })
      } catch (error) {
        logger.error('Error parsing address', error)
        return null
      }
    },
    [isLoaded]
  )

  /**
   * Gets search options for Places Autocomplete based on country
   */
  const getSearchOptions = useCallback(
    (countryCode?: string): { country?: string; types?: string[] } => {
      const options: { country?: string; types?: string[] } = {
        types: ['geocode'], // Use geocode for address-focused searches
      }

      if (countryCode) {
        options.country = countryCode.toLowerCase()
      }

      return options
    },
    []
  )

  return {
    isLoaded,
    loadError,
    getPlaceSuggestions,
    parseAddressComponents,
    getSearchOptions,
  }
}
